import React, { useEffect, useRef, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useGoogleMaps } from "components/GoogleMapsLoader";
import LoadingSpinner from "components/LoadingSpinner";
import ambulanceIcon from "assets/img/dashboards/OMNIDOC.png";
import usericon from "assets/img/user.png";
import missionicon from "assets/img/mission.png";
import basimg from "assets/img/_Barre du bas.png";
import Barre1 from "assets/img/Barre latérale 1.png";
import Barre2 from "assets/img/Barre latérale 2.png";
import stafficon from "assets/img/doc.png";

import axios from "axios";
import config, { BASE_URL } from "config/api";

export default function PrestationMap() {
  const { id } = useParams();
  const { loaded } = useGoogleMaps();
  const [targetPosition, setTargetPosition] = useState(null); // Mission
  const [userPosition, setUserPosition] = useState(null); // Utilisateur
  const [vehiclePosition, setVehiclePosition] = useState(null); // Véhicule
  const [trackerId, setTrackerId] = useState(null);
  const [userId, setUserId] = useState(null); // Médecin / Infirmier
  const [staffPosition, setStaffPosition] = useState(null); // Position staff
  const [geoError, setGeoError] = useState(null); // Ajout pour message d'erreur
  // Nouveaux états pour l'avis
  const [showReview, setShowReview] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [reviewSent, setReviewSent] = useState(false);
  const [reviewError, setReviewError] = useState(null);
  const [missionId, setMissionId] = useState(null); // Ajout missionId
  const [fetchError, setFetchError] = useState(null); // Pour afficher les erreurs de fetch

  // Fonction pour récupérer la position du véhicule
  const fetchVehicleLocation = async (trackerId) => {
    try {
      const data = {
        // Utiliser le hash depuis la config (variable d'environnement)
        hash: config.navixy.hash,
        tracker_id: trackerId,
      };
     
      const response = await axios.post(
        `${config.navixy.apiUrl}/tracker/get_state`,
        data,
        {
          headers: {
            'Content-Type': 'application/json',
          },
          // Ne pas envoyer de cookies/credentials vers Navixy pour éviter les erreurs CORS
          withCredentials: false,
        }
      );
      
      if (response.data?.state?.gps?.location) {
        return {
          lat: response.data.state.gps.location.lat,
          lng: response.data.state.gps.location.lng
        };
      }
      // Si aucune position trouvée, retourne null
      return null;
    } catch (error) {
      console.error("[fetchVehicleLocation] Erreur:", error);
      return null;
    }
  };

  // Fonction pour récupérer la position du staff (médecin / infirmier) via users/:id/loc
  const fetchStaffLocation = async (uId) => {
    try {
      const res = await axios.get(`${BASE_URL}/users/${uId}/loc`);
      const payload = res.data;
      const locVal = (payload && typeof payload === 'object' && (payload.lat || payload.lng || payload.loc)) ? (payload.loc ?? payload) : payload;
      if (!locVal) return null;
      if (typeof locVal === "object" && locVal.lat != null && locVal.lng != null) {
        return { lat: Number(locVal.lat), lng: Number(locVal.lng) };
      }
      if (
        typeof locVal === "string" &&
        /^-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/.test(locVal.trim())
      ) {
        const [lat, lng] = locVal.split(',').map(s => Number(s.trim()));
        return { lat, lng };
      }
      return null;
    } catch (error) {
      console.error("[fetchStaffLocation] Erreur:", error);
      return null;
    }
  };

  // 1. Récupérer loc et tracker depuis /map/:id
  useEffect(() => {
    async function fetchMapData() {
      try {
        const res = await axios.get(`${BASE_URL}/map/${id}`);
        // Mission
        if (res.data && res.data.fin === 1) {
          if (res.data.missionid) setMissionId(res.data.missionid);
          setShowReview(true);
          return; // On ne continue pas, on ne charge pas la carte
        }
        if (res.data.loc && typeof res.data.loc === "object") {
          setTargetPosition(res.data.loc);
        } else if (
          res.data.loc &&
          typeof res.data.loc === "string" &&
          /^-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/.test(res.data.loc.trim())
        ) {
          // loc est une chaîne de type "lat,lng"
          const [lat, lng] = res.data.loc.split(',').map(s => Number(s.trim()));
          setTargetPosition({ lat, lng });
        }
        // Tracker
        if (res.data.tracker_id) {
          setTrackerId(res.data.tracker_id);
        } else {
          setTrackerId(null);
          setVehiclePosition(null);
        }
        if (res.data.userid) {
          setUserId(res.data.userid);
        }
        if (res.data.missionid) setMissionId(res.data.missionid);
        setFetchError(null);
      } catch (err) {
        setTargetPosition(null);
        console.error("[fetchMapData] Erreur:", err);
        // Redirection si erreur 404
        if (err.response && err.response.status === 404) {
         
          window.location.href = "https://omnidoc.ma/";
        }
        setFetchError("Erreur lors du chargement de la carte. Veuillez réessayer.");
        setTimeout(() => setFetchError(null), 5000);
      }
    }
    if (loaded) fetchMapData();
  }, [id, loaded]);

  // Polling toutes les 5 secondes pour vérifier fin=1
  useEffect(() => {
    if (!loaded) return;
    let intervalId;
    async function pollFin() {
      try {
        const res = await axios.get(`${BASE_URL}/map/${id}`);
        if (res.data && res.data.fin === 1) {
          if (res.data.missionid) setMissionId(res.data.missionid);
          setShowReview(true);
        }
        setFetchError(null);
      } catch (err) {
        // Optionnel : gérer l'erreur
        setFetchError("Erreur lors de la mise à jour de la carte. Veuillez réessayer.");
        setTimeout(() => setFetchError(null), 5000);
      }
    }
    intervalId = setInterval(pollFin, 5000);
    return () => clearInterval(intervalId);
  }, [id, loaded]);

  // 2. Récupérer la position du véhicule dès qu'on a le trackerId
  useEffect(() => {
    if (!trackerId || showReview) return;
    let isMounted = true;
    let intervalId;

    const updateVehicle = () => {
      fetchVehicleLocation(trackerId).then((pos) => {
        if (isMounted) {
          if (pos) {
            setVehiclePosition(pos);
           
          } else {
            console.warn("[useEffect] Pas de position véhicule trouvée");
          }
        }
      });
    };

    updateVehicle(); // Premier fetch immédiat
    intervalId = setInterval(updateVehicle, 10000); // Toutes les 10 secondes

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [trackerId, showReview]);

  // Nettoyer la position véhicule si le tracker devient nul
  useEffect(() => {
    if (!trackerId) {
      setVehiclePosition(null);
    }
  }, [trackerId]);

  // 2.bis. Récupérer la position du staff dès qu'on a le userId (toutes les 5 secondes)
  useEffect(() => {
    if (!userId || showReview) return;
    let isMounted = true;
    let intervalId;

    const updateStaff = () => {
      fetchStaffLocation(userId).then((pos) => {
        if (!isMounted) return;
        if (pos) {
          setStaffPosition(pos);
        }
      });
    };

    updateStaff();
    intervalId = setInterval(updateStaff, 10000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [userId, showReview]);

  // 3. Récupérer la position de l'utilisateur
  useEffect(() => {
    if (!navigator.geolocation) {
      setGeoError("La géolocalisation n'est pas supportée par votre appareil.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const pos = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        setUserPosition(pos);
        setGeoError(null);
        // Vérifier si c'est la première autorisation
        if (!localStorage.getItem('geoAuthorized')) {
          localStorage.setItem('geoAuthorized', '1');
          window.location.reload();
        }
    
      },
      (error) => {
        setUserPosition(null);
        // Centrer la carte sur Casablanca si la géoloc échoue
        setTargetPosition({ lat: 33.5731, lng: -7.5898 }); // Casablanca
        if (error.code === 1) {
          setGeoError("La géolocalisation est désactivée ou refusée. La carte est centrée sur Casablanca.");
        } else {
          setGeoError("Impossible d'accéder à votre position. La carte est centrée sur Casablanca.");
        }
        console.error("[useEffect] Erreur geolocation:", error);
      },
      { enableHighAccuracy: true }
    );
  }, []);

  useEffect(() => {
   
  }, [targetPosition, userPosition, vehiclePosition, trackerId, staffPosition, userId]);

  if (!loaded) return <LoadingSpinner message="Chargement de la carte..." size="md" fullHeight />;

  // Affichage de l'interface d'avis si showReview
  if (showReview) {
    return (
      <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#f5f5f5'}}>
        <h2>Laissez votre avis sur la prestation</h2>
        <div style={{display: 'flex', flexDirection: 'row', margin: '16px 0'}}>
          {[1,2,3,4,5].map((star) => (
            <span
              key={star}
              style={{
                fontSize: 40,
                color: (hoverRating || rating) >= star ? '#FFD700' : '#ccc',
                cursor: 'pointer',
                transition: 'color 0.2s'
              }}
              onMouseEnter={() => setHoverRating(star)}
              onMouseLeave={() => setHoverRating(0)}
              onClick={() => setRating(star)}
              role="button"
              aria-label={`Donner ${star} étoile${star > 1 ? 's' : ''}`}
            >
              ★
            </span>
          ))}
        </div>
        <textarea
          placeholder="Décrivez votre expérience..."
          value={reviewText}
          onChange={e => setReviewText(e.target.value)}
          rows={4}
          style={{width: 300, padding: 8, borderRadius: 4, border: '1px solid #ccc', marginBottom: 16}}
        />
        <button
          style={{padding: '10px 24px', borderRadius: 4, background: '#1976d2', color: '#fff', border: 'none', fontSize: 16, cursor: 'pointer'}}
          onClick={async () => {
            setReviewError(null);
            if (rating === 0) {
              setReviewError("Merci de donner une note.");
              return;
            }
            try {
              // Envoi à l'API /avis avec les bons champs
              const avisPayload = {
                missionid: missionId,
                etoile: rating,
                avi: reviewText
              };
              
              await axios.post(`${BASE_URL}/avis`, avisPayload);
              setReviewSent(true);
              // Redirection selon la note
              setTimeout(() => {
                if (rating >= 4) {
                  window.location.href = 'https://g.page/r/CWTY_PPuJMNQEBM/review';
                } else {
                  window.location.href = 'https://omnidoc.ma/';
                }
              }, 1200); // Laisse le temps d'afficher le message de succès
            } catch (e) {
              console.error('[DEBUG] Erreur avis POST /avis', e);
              setReviewError("Erreur lors de l'envoi de l'avis.");
            }
          }}
          disabled={reviewSent}
        >
          {reviewSent ? "Avis envoyé !" : "Envoyer mon avis"}
        </button>
        {reviewError && <div style={{color: 'red', marginTop: 8}}>{reviewError}</div>}
        {reviewSent && <div style={{color: 'green', marginTop: 8}}>Merci pour votre retour !</div>}
      </div>
    );
  }

  return (
    <>
      {fetchError && (
        <div style={{position: 'fixed', top: 0, left: 0, width: '100vw', background: '#f44336', color: '#fff', zIndex: 2000, padding: 12, textAlign: 'center'}}>
          {fetchError}
          <button style={{marginLeft: 16, background: 'transparent', border: 'none', color: '#fff', fontWeight: 'bold', fontSize: 18, cursor: 'pointer'}} onClick={() => setFetchError(null)}>×</button>
        </div>
      )}
      <style>{`
        @media (max-width: 768px) {
          .prestation-layout { flex-direction: column !important; }
          .prestation-bar-left, .prestation-bar-right { display: none !important; }
          .prestation-map { width: 100vw !important; height: 80vh !important; }
          .prestation-bar-bottom { display: flex !important; }
        }
        @media (min-width: 769px) {
          .prestation-layout { flex-direction: row !important; }
          .prestation-bar-left, .prestation-bar-right { display: flex !important; }
          .prestation-map { width: 60vw !important; height: 100vh !important; }
          .prestation-bar-bottom { display: none !important; }
        }
      `}</style>
      <div className="prestation-layout" style={{ display: 'flex', width: '100vw', height: '100vh' }}>
        {/* Barre gauche (desktop) */}
        <a href="https://qr1.be/TT2G" className="prestation-bar-left" style={{ width: '20vw', height: '100vh', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          
          <img
            src={Barre1}
            alt="Mission"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain', // <-- voir toute l'image
              display: 'block',
              background: '#f5f5f5' // ou une autre couleur si tu veux
            }}
          />
        </a>
        {/* Carte centrale */}
        <div className="prestation-map" style={{ width: '60vw', height: '100vh', position: 'relative' }}>
          {geoError && (
            <div style={{color: 'red', position: 'absolute', top: 10, left: 10, zIndex: 1000, background: '#fff', padding: 8, borderRadius: 4, border: '1px solid #f00'}}>
              {geoError}
            </div>
          )}
          <MapDisplay 
            key={targetPosition ? `${targetPosition.lat || ''},${targetPosition.lng || ''}` : 'default'}
            targetPosition={targetPosition}
            userPosition={userPosition}
            vehiclePosition={vehiclePosition}
            staffPosition={staffPosition}
          />
        </div>
        {/* Barre droite (desktop) */}
        <a href="https://qr1.be/TT2G" className="prestation-bar-right" style={{ width: '20vw', height: '100vh', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img
            src={Barre2}
            alt="Véhicule"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain', // <-- voir toute l'image
              display: 'block',
              background: '#f5f5f5' // ou une autre couleur si tu veux
            }}
          />
        </a>
        {/* Barre en bas (mobile/tablette) */}
        <a href="https://qr1.be/TT2G" className="prestation-bar-bottom" style={{ width: '100vw', height: '20vh', background: 'linear-gradient(90deg, #006133 0%, #EE5D50 100%)', display: 'none', alignItems: 'center', justifyContent: 'space-around', position: 'absolute', bottom: 0, left: 0, zIndex: 10 }}>
          <img
            src={basimg}
            alt="Mission"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain', // <-- voir toute l'image
              display: 'block',
              background: '#f5f5f5' // ou une autre couleur si tu veux
            }}
          />
        </a>
      </div>
    </>
  );
}

function MapDisplay({ targetPosition, userPosition, vehiclePosition, staffPosition }) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef({});
  const lastVehiclePos = useRef(null);
  const animationFrame = useRef(null);

  const center = useMemo(() => {
    return vehiclePosition || targetPosition || staffPosition || userPosition || { lat: 33.5939313, lng: -7.6387418 };
  }, [vehiclePosition, targetPosition, staffPosition, userPosition]);

  // Initialiser la carte une seule fois
  useEffect(() => {
    if (!window.google || !window.google.maps) return;
    if (!mapInstance.current) {
      mapInstance.current = new window.google.maps.Map(mapRef.current, {
        center,
        zoom: 15,
        styles: [
          { "elementType": "geometry", "stylers": [{ "color": "#ebe3cd" }] },
          { "elementType": "labels.text.fill", "stylers": [{ "color": "#523735" }] },
          { "elementType": "labels.text.stroke", "stylers": [{ "color": "#f5f1e6" }] },
          { "featureType": "administrative", "elementType": "geometry.stroke", "stylers": [{ "color": "#c9b2a6" }] },
          { "featureType": "administrative.land_parcel", "elementType": "geometry.stroke", "stylers": [{ "color": "#dcd2be" }] },
          { "featureType": "administrative.land_parcel", "elementType": "labels.text.fill", "stylers": [{ "color": "#ae9e90" }] },
          { "featureType": "landscape.natural", "elementType": "geometry", "stylers": [{ "color": "#dfd2ae" }] },
          { "featureType": "poi", "elementType": "geometry", "stylers": [{ "color": "#dfd2ae" }] },
          { "featureType": "poi", "elementType": "labels.text.fill", "stylers": [{ "color": "#93817c" }] },
          { "featureType": "poi.park", "elementType": "geometry.fill", "stylers": [{ "color": "#006133" }] },
          { "featureType": "poi.park", "elementType": "labels.text.fill", "stylers": [{ "color": "#447530" }] },
          { "featureType": "road", "elementType": "geometry", "stylers": [{ "color": "#f5f1e6" }] },
          { "featureType": "road.arterial", "elementType": "geometry", "stylers": [{ "color": "#fdfcf8" }] },
          { "featureType": "road.highway", "elementType": "geometry", "stylers": [{ "color": "#f8c967" }] },
          { "featureType": "road.highway", "elementType": "geometry.stroke", "stylers": [{ "color": "#e9bc62" }] },
          { "featureType": "road.highway.controlled_access", "elementType": "geometry", "stylers": [{ "color": "#e98d58" }] },
          { "featureType": "road.highway.controlled_access", "elementType": "geometry.stroke", "stylers": [{ "color": "#db8555" }] },
          { "featureType": "road.local", "elementType": "labels.text.fill", "stylers": [{ "color": "#806b63" }] },
          { "featureType": "transit.line", "elementType": "geometry", "stylers": [{ "color": "#dfd2ae" }] },
          { "featureType": "transit.line", "elementType": "labels.text.fill", "stylers": [{ "color": "#8f7d77" }] },
          { "featureType": "transit.line", "elementType": "labels.text.stroke", "stylers": [{ "color": "#ebe3cd" }] },
          { "featureType": "transit.station", "elementType": "geometry", "stylers": [{ "color": "#dfd2ae" }] },
          { "featureType": "water", "elementType": "geometry.fill", "stylers": [{ "color": "#b9d3c2" }] },
          { "featureType": "water", "elementType": "labels.text.fill", "stylers": [{ "color": "#92998d" }] }
        ],
      });

    } else {
      mapInstance.current.setCenter(center);
     
    }
  }, [center]);

  // Adapter le cadrage pour afficher tous les marqueurs connus (mission, staff, voiture, utilisateur)
  useEffect(() => {
    if (!window.google || !window.google.maps) return;
    if (!mapInstance.current) return;
    const points = [vehiclePosition, targetPosition, staffPosition, userPosition].filter(Boolean);
    if (points.length === 0) return;
    if (points.length === 1) {
      mapInstance.current.setCenter(points[0]);
      mapInstance.current.setZoom(15);
      return;
    }
    const bounds = new window.google.maps.LatLngBounds();
    points.forEach(p => bounds.extend(p));
    mapInstance.current.fitBounds(bounds, 64); // padding
  }, [vehiclePosition, targetPosition, staffPosition, userPosition]);

  // Fonction utilitaire pour créer ou mettre à jour un marker
  const updateMarker = (key, position, iconUrl, title) => {
    if (!position) {
      if (markersRef.current[key]) {
        markersRef.current[key].setMap(null);
        markersRef.current[key] = null;
      }
      return;
    }
    if (!markersRef.current[key]) {
      markersRef.current[key] = new window.google.maps.Marker({
        position,
        map: mapInstance.current,
        title,
        icon: {
          url: iconUrl,
          scaledSize: new window.google.maps.Size(40, 40)
        }
      });
      
    } else {
      markersRef.current[key].setPosition(position);
      
    }
  };

  // Marqueur mission
  useEffect(() => {
    updateMarker("mission", targetPosition, missionicon, "Mission");
  }, [targetPosition]);
  // Marqueur utilisateur
  useEffect(() => {
    updateMarker("user", userPosition, usericon, "Vous");
  }, [userPosition]);
  // Marqueur staff (médecin / infirmier)
  useEffect(() => {
    updateMarker("staff", staffPosition, stafficon, "Personnel médical");
  }, [staffPosition]);
  // Marqueur véhicule
  useEffect(() => {
    updateMarker("vehicle", vehiclePosition, ambulanceIcon, "Véhicule");
  }, [vehiclePosition]);

  // Animation du marqueur véhicule
  useEffect(() => {
    if (!window.google || !window.google.maps) return;
    // Si pas de position précédente ou pas de nouvelle position, pas d'animation
    if (!vehiclePosition) return;
    const marker = markersRef.current["vehicle"];
    if (!marker) return;
    const start = lastVehiclePos.current || vehiclePosition;
    const end = vehiclePosition;
    const duration = 1000; // 1 seconde d'animation
    const startTime = performance.now();

    function lerp(a, b, t) {
      return a + (b - a) * t;
    }

    function animate(now) {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / duration, 1);
      const lat = lerp(start.lat, end.lat, t);
      const lng = lerp(start.lng, end.lng, t);
      marker.setPosition({ lat, lng });
      if (t < 1) {
        animationFrame.current = requestAnimationFrame(animate);
      } else {
        marker.setPosition(end);
        lastVehiclePos.current = end;
      }
    }

    // Lance l'animation
    if (lastVehiclePos.current && (start.lat !== end.lat || start.lng !== end.lng)) {
      cancelAnimationFrame(animationFrame.current);
      animationFrame.current = requestAnimationFrame(animate);
    } else {
      marker.setPosition(end);
      lastVehiclePos.current = end;
    }

    return () => {
      cancelAnimationFrame(animationFrame.current);
    };
  }, [vehiclePosition]);

  // Nettoyage des marqueurs à la destruction
  useEffect(() => {
    return () => {
      Object.values(markersRef.current).forEach(marker => {
        if (marker) marker.setMap(null);
      });
      markersRef.current = {};
    };
  }, []);

  return <div ref={mapRef} style={{ width: "100%", height: "100%" }} />;
}
